import React, { useContext, useState, useEffect } from 'react';
import { fileUrl } from '../utils/fileUrl';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext, API } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from './ui/sheet';
import {
  LayoutDashboard,
  Car,
  FileText,
  Settings,
  LogOut,
  Menu,
  User,
  Calendar,
  CalendarDays,
  Plus,
  CreditCard,
  Tag,
  UserPlus,
  Users,
  Target,
  Briefcase,
  DollarSign,
  Building2,
  Navigation
} from 'lucide-react';

const NavLink = ({ item, mobile = false, location, setMobileMenuOpen }) => {
  const Icon = item.icon;
  const isActive = location.pathname === item.path;
  
  return (
    <Link
      to={item.path}
      onClick={() => mobile && setMobileMenuOpen(false)}
      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg nav-link ${
        isActive
          ? 'bg-blue-50 text-blue-600 font-medium'
          : 'text-gray-700 hover:bg-gray-100'
      }`}
      data-testid={`nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
    >
      <Icon className="w-5 h-5" />
      <span>{item.label}</span>
    </Link>
  );
};

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState('');

  useEffect(() => {
    const fetchProfilePhoto = async () => {
      if (!user) return;
      try {
        const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
        const response = await axios.get(`${API}/auth/me`);
        if (response.data.profile_photo_url) {
          setProfilePhoto(response.data.profile_photo_url);
        }
      } catch (error) {
        console.error('Failed to load profile photo');
      }
    };

    fetchProfilePhoto();
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin';
  const isRider = user?.role === 'rider';
  const isDriver = user?.role === 'driver';
  const isConcierge = user?.role === 'concierge';

  const menuItems = isAdmin ? [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/notifications', label: 'Notifications', icon: User },
    { path: '/admin/rides', label: 'Manage Rides', icon: Car },
    { path: '/admin/drivers', label: 'Manage Drivers', icon: User },
    { path: '/admin/driver-applications', label: 'Driver Applications', icon: UserPlus },
    { path: '/admin/companies', label: 'Manage Companies', icon: Building2 },
    { path: '/admin/concierges', label: 'Manage Concierges', icon: Briefcase },
    { path: '/admin/drive-your-car', label: 'Drive Your Car', icon: Car },
    { path: '/admin/create-ride', label: 'Create Client Ride', icon: UserPlus },
    { path: '/admin/zelle-payments', label: 'Zelle Payments', icon: DollarSign },
    { path: '/admin/coupons', label: 'Coupons', icon: Tag },
    { path: '/admin/events', label: 'Events Calendar', icon: CalendarDays },
    { path: '/admin/live-tracking', label: 'Live Event Tracking', icon: Navigation },
    { path: '/admin/blog', label: 'Blog Management', icon: FileText },
    { path: '/admin/leads', label: 'Lead Generation', icon: Target },
    { path: '/company', label: 'My Company', icon: Building2 },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/rides/new', label: 'New Ride', icon: Plus },
    { path: '/rides', label: 'All Rides', icon: Car },
    { path: '/reports/weekly', label: 'Weekly Report', icon: Calendar },
    { path: '/reports/monthly', label: 'Monthly Report', icon: CalendarDays },
    { path: '/settings', label: 'Settings', icon: Settings },
  ] : isConcierge ? [
    { path: '/concierge/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/concierge/new-ride', label: 'Post New Ride', icon: Plus },
    { path: '/settings', label: 'Settings', icon: Settings },
  ] : isRider ? [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/rider/request', label: 'Request a Ride', icon: Plus },
    { path: '/rides/list', label: 'My Rides', icon: Car },
    { path: '/payment-settings', label: 'Payment Methods', icon: CreditCard },
  ] : [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/company', label: 'My Company', icon: Building2 },
    { path: '/subscription', label: 'Subscription', icon: User },
    { path: '/ride-requests', label: 'Ride Requests', icon: FileText },
    { path: '/rides/available', label: 'Available Rides', icon: Car },
    { path: '/driver/events', label: 'My Events', icon: CalendarDays },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/create-client-ride', label: 'Create Client Ride', icon: UserPlus },
    { path: '/rides/new', label: 'New Ride', icon: Plus },
    { path: '/rides', label: 'My Rides', icon: Car },
    { path: '/reports/weekly', label: 'Weekly Report', icon: Calendar },
    { path: '/reports/monthly', label: 'Monthly Report', icon: CalendarDays },
    { path: '/settings', label: 'Settings', icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-black border-b border-gray-800 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-4">
              {/* Mobile menu button */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" className="text-white hover:bg-gray-800" data-testid="mobile-menu-button">
                    <Menu className="h-6 w-6" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0">
                  <div className="flex flex-col h-full">
                    <div className="p-4 border-b bg-black">
                      <img 
                        src="/onyx-reserve-logo.png" 
                        alt="Onyx Reserve" 
                        className="h-[72px] w-auto"
                      />
                    </div>
                    <nav className="flex-1 p-4 space-y-1">
                      {menuItems.map((item) => (
                        <NavLink key={item.path} item={item} mobile location={location} setMobileMenuOpen={setMobileMenuOpen} />
                      ))}
                    </nav>
                  </div>
                </SheetContent>
              </Sheet>

              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="/onyx-reserve-logo.png" 
                  alt="Onyx Reserve" 
                  className="h-[72px] w-auto"
                />
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 text-white hover:bg-gray-800" data-testid="user-menu-button">
                    <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden">
                      {profilePhoto ? (
                        <img 
                          src={fileUrl(profilePhoto)}
                          alt="Profile" 
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.error('Profile photo failed to load:', e.target.src);
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<svg class="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"/></svg>';
                          }}
                        />
                      ) : (
                        <User className="w-5 h-5 text-gold-500" />
                      )}
                    </div>
                    <span className="hidden sm:inline text-sm font-medium text-white">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="text-sm text-gray-700 font-medium">
                    {user?.email}
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-xs text-gray-500">
                    Role: {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
                  </DropdownMenuItem>
                  {!isRider && (
                    <>
                      <DropdownMenuItem
                        onClick={() => navigate('/profile')}
                        className="cursor-pointer"
                        data-testid="profile-button"
                      >
                        <User className="w-4 h-4 mr-2" />
                        My Profile
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="text-red-600 cursor-pointer"
                    data-testid="logout-button"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <nav className="bg-white rounded-lg shadow-sm p-4 space-y-1 sticky top-24">
              {menuItems.map((item) => (
                <NavLink key={item.path} item={item} location={location} setMobileMenuOpen={setMobileMenuOpen} />
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <Outlet />
          </main>
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-gray-100 border-t border-gray-200 mt-8 py-6 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-sm text-gray-600">
            Powered by MMTA, INC – Licensed & Insured Black Car and Limousine Company
          </p>
          <p className="text-xs text-gray-400 mt-1">
            &copy; {new Date().getFullYear()} Onyx Reserve. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
